import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Link } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';
import { sanitizeRedirectPath } from '../utils/redirectAfterLogin';

export default function ResetPassword() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  const { confirmPasswordReset, requestPasswordReset } = useAuth();

  const email = location.state?.email || '';
  // Set when a sign-up turned into account recovery; login then returns there.
  const redirectTo = sanitizeRedirectPath(location.state?.redirect);

  const [formData, setFormData] = useState({
    code: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      setError(t('resetPassword.passwordMismatch', 'Passwords do not match'));
      return;
    }

    // Validate password strength
    if (formData.newPassword.length < 8) {
      setError(t('resetPassword.passwordTooShort', 'Password must be at least 8 characters long'));
      return;
    }

    setLoading(true);

    try {
      await confirmPasswordReset(email, formData.code, formData.newPassword);
      setSuccess(
        t('resetPassword.success', 'Password reset successfully! Redirecting to login...')
      );
      setTimeout(() => {
        navigate(redirectTo ? `/login?redirect=${encodeURIComponent(redirectTo)}` : '/login');
      }, 2000);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : t('resetPassword.resetFailed');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!email) {
      setError(t('resetPassword.emailRequired', 'Email is required. Please start over.'));
      return;
    }

    setError('');
    setSuccess('');
    setResending(true);

    try {
      await requestPasswordReset(email);
      setSuccess(t('resetPassword.codeSent', 'Verification code sent! Check your email.'));
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : t('resetPassword.resendFailed');
      setError(errorMessage);
    } finally {
      setResending(false);
    }
  };

  if (!email) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-warm-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-xl shadow-brand p-8 space-y-6">
            <div className="text-center">
              <h2 className="text-h2 font-bold text-charcoal">
                {t('resetPassword.title', 'Reset Password')}
              </h2>
              <p className="mt-3 text-body text-charcoal/70">
                {t(
                  'resetPassword.noEmail',
                  'No email provided. Please request a password reset first.'
                )}
              </p>
              <div className="mt-6">
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                >
                  {t('resetPassword.requestReset', 'Request password reset')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-warm-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Card Container */}
        <div className="bg-white rounded-xl shadow-brand p-8 space-y-6">
          {/* Header */}
          <div className="text-center">
            <h2 className="text-h2 font-bold text-charcoal">
              {t('resetPassword.title', 'Reset Password')}
            </h2>
            <p className="mt-3 text-body text-charcoal/70">
              {t(
                'resetPassword.description',
                'Enter the verification code sent to your email and choose a new password.'
              )}
            </p>
            <p className="mt-2 text-sm text-charcoal/60">{email}</p>
          </div>

          <form className="space-y-5" onSubmit={handleSubmit}>
            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-4">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
            )}

            {success && (
              <div className="rounded-lg bg-green-50 border border-green-200 p-4">
                <p className="text-sm font-medium text-green-800">{success}</p>
              </div>
            )}

            {/* Verification Code Input */}
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-charcoal mb-2">
                {t('resetPassword.code', 'Verification Code')}
              </label>
              <input
                id="code"
                name="code"
                type="text"
                required
                value={formData.code}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-soft-gray rounded-lg text-charcoal placeholder-charcoal/40 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow"
                placeholder={t('resetPassword.codePlaceholder', '123456')}
              />
            </div>

            {/* New Password Input */}
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-charcoal mb-2">
                {t('resetPassword.newPassword', 'New Password')}
              </label>
              <input
                id="newPassword"
                name="newPassword"
                type="password"
                autoComplete="new-password"
                required
                value={formData.newPassword}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-soft-gray rounded-lg text-charcoal placeholder-charcoal/40 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow"
                placeholder={t('resetPassword.passwordPlaceholder', '••••••••')}
              />
            </div>

            {/* Confirm Password Input */}
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-charcoal mb-2"
              >
                {t('resetPassword.confirmPassword', 'Confirm New Password')}
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-soft-gray rounded-lg text-charcoal placeholder-charcoal/40 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow"
                placeholder={t('resetPassword.passwordPlaceholder', '••••••••')}
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center items-center py-3 px-6 border border-transparent text-body font-semibold rounded-lg text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading
                ? t('resetPassword.resetting', 'Resetting...')
                : t('resetPassword.resetButton', 'Reset Password')}
            </button>

            {/* Resend Code Link */}
            <div className="text-center">
              <button
                type="button"
                onClick={handleResendCode}
                disabled={resending}
                className="text-sm font-medium text-primary hover:text-primary/80 transition-colors disabled:opacity-50"
              >
                {resending
                  ? t('resetPassword.resending', 'Sending...')
                  : t('resetPassword.resendCode', "Didn't receive code? Resend")}
              </button>
            </div>

            {/* Back to login link */}
            <div className="text-center">
              <Link
                to="/login"
                className="text-sm font-medium text-charcoal/60 hover:text-charcoal transition-colors"
              >
                {t('resetPassword.backToLogin', 'Back to login')}
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
